import { parse, test } from './expr.js';
import { SYS_CONCEPT, SYS_DRUG, SYS_FORM, } from './fhir.js';
/** Hours from one FHIR dateTime to another (both carry their offset). */
export const hoursBetween = (from, to) => (Date.parse(to) - Date.parse(from)) / 3_600_000;
/** Recorded observations of a concept within the last `hours` before `now`, oldest first. */
function inWindow(s, concept, hours) {
    const now = s.now;
    if (!now)
        return [];
    return observationsOf(s, concept)
        .filter((o) => {
        const h = hoursBetween(o.effectiveDateTime, now);
        return h >= 0 && h <= hours;
    })
        .reverse();
}
const dayNumber = (isoDate) => {
    const y = Number(isoDate.slice(0, 4));
    const m = Number(isoDate.slice(5, 7) || '1');
    const d = Number(isoDate.slice(8, 10) || '1');
    return Math.floor(Date.UTC(y, m - 1, d) / 86_400_000);
};
/** Whole days from `from` to `to` (both FHIR date/dateTime; clinic dates). */
export const daysBetween = (from, to) => dayNumber(to.slice(0, 10)) - dayNumber(from.slice(0, 10));
export function conceptOf(o) {
    return o.code.coding?.find((c) => c.system === SYS_CONCEPT)?.code ?? null;
}
export function drugOf(m) {
    return m.medicationCodeableConcept.coding?.find((c) => c.system === SYS_DRUG)?.code ?? null;
}
export function observationValue(o) {
    if (o.valueQuantity)
        return o.valueQuantity.value;
    if (o.valueInteger !== undefined)
        return o.valueInteger;
    if (o.valueBoolean !== undefined)
        return o.valueBoolean;
    if (o.valueCodeableConcept)
        return o.valueCodeableConcept.coding?.[0]?.code ?? o.valueCodeableConcept.text ?? null;
    if (o.valueString !== undefined)
        return o.valueString;
    if (o.valueDateTime !== undefined)
        return o.valueDateTime;
    return null;
}
/** Daily total of one prescription: dose x frequency per day (frequency defaults to once a day). */
export function dailyDose(m) {
    const d = m.dosageInstruction?.[0];
    const dose = d?.doseAndRate?.[0]?.doseQuantity?.value ?? 0;
    const rep = d?.timing?.repeat;
    const perDay = rep?.frequency && (!rep.periodUnit || rep.periodUnit === 'd')
        ? rep.frequency / (rep.period ?? 1)
        : 1;
    return dose * perDay;
}
const formOf = (e) => {
    for (const t of e.type ?? [])
        for (const c of t.coding ?? [])
            if (c.system === SYS_FORM)
                return c.code ?? null;
    return null;
};
const astCache = new Map();
const testAst = (src) => {
    let a = astCache.get(src);
    if (!a) {
        a = parse(src);
        astCache.set(src, a);
    }
    return a;
};
const testValue = (ast, v) => test(ast, { value: v });
/** Recorded observations of a concept, newest first; only the open episode's when asked. */
function inEpisode(s, concept, programme) {
    const all = observationsOf(s, concept);
    if (!programme)
        return all;
    const ep = enrolment(s, programme);
    if (!ep)
        return [];
    const encs = new Set((s.encounters ?? [])
        .filter((e) => (e.episodeOfCare ?? []).some((r) => r.reference === `EpisodeOfCare/${ep.id}`))
        .map((e) => `Encounter/${e.id}`));
    return all.filter((o) => encs.has(o.encounter?.reference ?? ''));
}
const usable = (o) => o.status !== 'entered-in-error' && o.status !== 'cancelled';
function observationsOf(s, concept) {
    return s.observations
        .filter((o) => usable(o) && conceptOf(o) === concept)
        .sort((a, b) => b.effectiveDateTime.localeCompare(a.effectiveDateTime));
}
const present = (v) => v !== null && v !== undefined && v !== '';
function enrolment(s, programme) {
    return s.episodes.find((e) => e.status === 'active' && e.type.some((t) => t.coding?.some((c) => c.code === programme)));
}
function resolve(spec, s) {
    switch (spec.from) {
        case 'form': {
            const v = s.form[spec.concept];
            return present(v) ? v : null;
        }
        case 'form_or_recent': {
            const v = s.form[spec.concept];
            if (present(v))
                return v;
            const last = observationsOf(s, spec.concept)[0];
            if (!last || daysBetween(last.effectiveDateTime, s.today) > spec.max_age_days)
                return null;
            return observationValue(last);
        }
        case 'previous': {
            const last = observationsOf(s, spec.concept).find((o) => daysBetween(o.effectiveDateTime, s.today) > 0);
            return last ? observationValue(last) : null;
        }
        case 'days_since_concept': {
            if (present(s.form[spec.concept]))
                return 0;
            const last = observationsOf(s, spec.concept)[0];
            return last ? daysBetween(last.effectiveDateTime, s.today) : null;
        }
        case 'medication_dose':
            return s.medications
                .filter((m) => m.status === 'active' && spec.drugs.includes(drugOf(m) ?? ''))
                .reduce((sum, m) => sum + dailyDose(m), 0);
        case 'days_since_med_change': {
            const dates = s.medications
                .filter((m) => m.status !== 'entered-in-error' && spec.drugs.includes(drugOf(m) ?? ''))
                .map((m) => m.authoredOn)
                .sort();
            const last = dates[dates.length - 1];
            return last ? daysBetween(last, s.today) : null;
        }
        case 'days_since_med_start': {
            const dates = s.medications
                .filter((m) => m.status === 'active' && spec.drugs.includes(drugOf(m) ?? ''))
                .map((m) => m.authoredOn)
                .sort();
            const first = dates[0];
            return first ? daysBetween(first, s.today) : null;
        }
        case 'enrolled':
            return !!enrolment(s, spec.programme);
        case 'months_since_enrolment': {
            const start = enrolment(s, spec.programme)?.period?.start;
            return start ? Math.floor(daysBetween(start, s.today) / 30.4375) : null;
        }
        case 'age_years': {
            const b = s.patient.birthDate;
            if (!b)
                return null;
            const [ty, tm, td] = s.today.split('-').map(Number);
            const y = Number(b.slice(0, 4));
            if (b.length < 10)
                return ty - y;
            const m = Number(b.slice(5, 7));
            const d = Number(b.slice(8, 10));
            return ty - y - (tm < m || (tm === m && td < d) ? 1 : 0);
        }
        case 'sex':
            return s.patient.gender;
        case 'age_days': {
            const b = s.patient.birthDate;
            return b && b.length >= 10 ? daysBetween(b, s.today) : null;
        }
        case 'mother_latest': {
            const m = s.mother;
            if (!m)
                return null;
            const last = m.observations
                .filter((o) => usable(o) && conceptOf(o) === spec.concept)
                .sort((a, b) => b.effectiveDateTime.localeCompare(a.effectiveDateTime))[0];
            if (!last || daysBetween(last.effectiveDateTime, s.today) > spec.max_age_days)
                return null;
            return observationValue(last);
        }
        case 'mother_any_where': {
            const ast = testAst(spec.test);
            return (s.mother?.observations ?? []).some((o) => usable(o) && conceptOf(o) === spec.concept && testValue(ast, observationValue(o)));
        }
        case 'count_form': {
            const ep = spec.programme ? enrolment(s, spec.programme) : undefined;
            if (spec.programme && !ep)
                return 0;
            return (s.encounters ?? []).filter((e) => e.status !== 'entered-in-error' &&
                formOf(e) === spec.form &&
                (!ep || (e.episodeOfCare ?? []).some((r) => r.reference === `EpisodeOfCare/${ep.id}`))).length;
        }
        case 'date_of_latest': {
            if (present(s.form[spec.concept]))
                return s.today;
            const last = observationsOf(s, spec.concept)[0];
            return last ? last.effectiveDateTime.slice(0, 10) : null;
        }
        case 'days_since_form': {
            const dates = (s.encounters ?? [])
                .filter((e) => e.status !== 'entered-in-error' && formOf(e) === spec.form)
                .map((e) => e.period.start)
                .sort();
            const last = dates[dates.length - 1];
            return last ? daysBetween(last, s.today) : null;
        }
        case 'latest': {
            const v = s.form[spec.concept];
            if (!spec.stored_only && present(v))
                return v;
            const last = inEpisode(s, spec.concept, spec.episode)[0];
            return last ? observationValue(last) : null;
        }
        case 'any_where': {
            const ast = testAst(spec.test);
            const v = s.form[spec.concept];
            if (!spec.stored_only && present(v) && testValue(ast, v))
                return true;
            return inEpisode(s, spec.concept, spec.episode).some((o) => testValue(ast, observationValue(o)));
        }
        case 'latest_in_window': {
            const v = s.form[spec.concept];
            if (!spec.stored_only && present(v))
                return v;
            const last = inWindow(s, spec.concept, spec.window_hours).pop();
            return last ? observationValue(last) : null;
        }
        case 'hours_since_value': {
            if (!s.now)
                return null;
            const f = s.form[spec.concept];
            const last = inWindow(s, spec.concept, spec.window_hours).pop();
            const v = present(f) ? f : last ? observationValue(last) : null;
            if (typeof v !== 'string' || Number.isNaN(Date.parse(v)))
                return null;
            const h = hoursBetween(v, s.now);
            return spec.signed ? h : Math.max(0, h);
        }
        case 'hours_since_first': {
            if (!s.now)
                return null;
            const first = inWindow(s, spec.concept, spec.window_hours).find((o) => observationValue(o) === true);
            if (first)
                return Math.max(0, hoursBetween(first.effectiveDateTime, s.now));
            return s.form[spec.concept] === true ? 0 : null;
        }
        case 'hours_at_current': {
            const now = s.now;
            if (!now)
                return null;
            const series = inWindow(s, spec.concept, spec.window_hours)
                .map((o) => [o.effectiveDateTime, observationValue(o)])
                .filter((x) => typeof x[1] === 'number');
            const f = s.form[spec.concept];
            if (typeof f === 'number')
                series.push([now, f]);
            const last = series[series.length - 1];
            if (!last)
                return null;
            const whole = Math.floor(last[1]);
            let since = last[0];
            for (let i = series.length - 1; i >= 0; i--) {
                const [at, v] = series[i];
                if (Math.floor(v) < whole)
                    break;
                since = at;
            }
            return Math.max(0, hoursBetween(since, now));
        }
    }
}
/** The rule context for this patient and visit, as the pack declares it. */
export function buildContext(pack, s) {
    const out = {};
    for (const spec of pack.context)
        out[spec.var] = resolve(spec, s);
    return out;
}
//# sourceMappingURL=context.js.map